import { useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Package,
  GripVertical,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AllocationConfigModal } from './allocation-config-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type AllocationConfig = {
  id: string;
  allocationId: string;
  unitsPerBooking: number;
  isRequired: boolean;
  priority: number;
  autoConsume: boolean;
  dayOffset: number;
  notes: string | null;
  allocation: {
    id: string;
    name: string;
    allocationType: string;
    totalUnits: number;
    consumedUnits: number;
    validFrom: Date;
    validTo: Date;
    availableUnits: number;
    partner: {
      id: string;
      companyName: string;
      partnerType: string;
    };
  };
};

interface AllocationConfigSectionProps {
  packageId: string;
  onRefresh?: () => void;
}

const allocationTypeLabels: Record<string, string> = {
  HOTEL_ROOM: 'Hotel',
  TOUR_SEAT: 'Tour',
  TRANSFER_VEHICLE: 'Transfer',
  FLIGHT_SEAT: 'Flight',
};

const allocationTypeColors: Record<string, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-green-100 text-green-700',
  TRANSFER_VEHICLE: 'bg-purple-100 text-purple-700',
  FLIGHT_SEAT: 'bg-orange-100 text-orange-700',
};

export function AllocationConfigSection({ packageId, onRefresh }: AllocationConfigSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AllocationConfig | undefined>();

  const {
    data: configs,
    isLoading,
    refetch,
  } = trpc.packageAllocationConfig.listByPackage.useQuery(packageId);

  const deleteMutation = trpc.packageAllocationConfig.delete.useMutation({
    onSuccess: () => {
      showSuccessToast('Allocation unlinked');
      refetch();
      onRefresh?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to unlink allocation');
    },
  });

  const handleAdd = () => {
    setEditingConfig(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (config: AllocationConfig) => {
    setEditingConfig(config);
    setIsModalOpen(true);
  };

  const handleDelete = (configId: string) => {
    if (confirm('Are you sure you want to unlink this allocation from the package?')) {
      deleteMutation.mutate(configId);
    }
  };

  const handleSuccess = () => {
    refetch();
    onRefresh?.();
  };

  const existingAllocationIds = configs?.map((c) => c.allocationId) || [];

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Package className="h-5 w-5" />
          Allocations
        </h2>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Link
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : configs && configs.length > 0 ? (
        <div className="space-y-2">
          {configs.map((config, index) => (
            <div
              key={config.id}
              className="rounded-lg border p-3 group hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Drag handle (future: implement drag & drop) */}
                <div className="flex flex-col items-center gap-1 pt-1">
                  <GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab" />
                  <span className="text-[10px] text-muted-foreground">{index + 1}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{config.allocation.name}</p>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        allocationTypeColors[config.allocation.allocationType] ||
                        'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {allocationTypeLabels[config.allocation.allocationType] ||
                        config.allocation.allocationType}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {config.allocation.partner.companyName}
                  </p>

                  {/* Config info */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                    <span className="text-muted-foreground">
                      {config.unitsPerBooking} unit{config.unitsPerBooking > 1 ? 's' : ''}/booking
                    </span>
                    {config.dayOffset !== 0 && (
                      <span className="text-muted-foreground">
                        Day {config.dayOffset > 0 ? `+${config.dayOffset}` : config.dayOffset}
                      </span>
                    )}
                    {config.isRequired ? (
                      <span className="flex items-center gap-0.5 text-green-600">
                        <CheckCircle className="h-3 w-3" />
                        Required
                      </span>
                    ) : (
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        <AlertCircle className="h-3 w-3" />
                        Optional
                      </span>
                    )}
                    {config.autoConsume && (
                      <span className="text-blue-600">Auto-consume</span>
                    )}
                  </div>

                  {/* Availability */}
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            config.allocation.availableUnits > 0
                              ? 'bg-green-500'
                              : 'bg-red-500'
                          }`}
                          style={{
                            width: `${
                              (config.allocation.availableUnits /
                                config.allocation.totalUnits) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {config.allocation.availableUnits}/{config.allocation.totalUnits} avail
                      </span>
                    </div>
                  </div>

                  {config.notes && (
                    <p className="text-xs text-muted-foreground mt-1 italic">
                      {config.notes}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(config)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Config
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(config.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Unlink
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Package className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No allocations linked</p>
          <p className="text-xs text-muted-foreground mt-1">
            Link supplier allocations to auto-consume on booking
          </p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <AllocationConfigModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          packageId={packageId}
          config={editingConfig}
          existingAllocationIds={existingAllocationIds}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
